/**
 * Grouped picker dropdown used for the size picker (apply bar + crop editor)
 * and the fine-art paper picker. Options come pre-grouped from
 * lib/sizePaper (sizePickerGroups / paperPickerGroups).
 *
 * Variants: 'dark' (charcoal trigger on the black apply bar) and 'light'
 * (outlined trigger on white panels). The popover is always light.
 *
 * Owner 2026-07-27: the popover renders position:FIXED at the trigger's
 * viewport rect so it escapes scroll containers (the Customise pane's
 * .cfg__scroll clipped it behind the pane). Scrolling anywhere outside the
 * popover closes it (a fixed popover would detach from its trigger).
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { MOBILE_QUERY } from '../../hooks/useMediaQuery';
import type { PickerGroup } from '../../lib/sizePaper';

export interface DropdownProps {
  groups: PickerGroup[];
  value: string | null;
  onChange: (key: string) => void;
  /** Trigger content (usually "label — price"). */
  buttonLabel: ReactNode;
  variant: 'dark' | 'light';
  ariaLabel: string;
  className?: string;
  /** Row to highlight (light green) and scroll to when the list opens. */
  highlightKey?: string | null;
  /** Badge shown on that row, e.g. "Largest for this photo". */
  highlightBadge?: string;
  /** Bump to open the list from OUTSIDE (the upsell button beside it). */
  openToken?: number;
}

export function Dropdown({
  groups,
  value,
  onChange,
  buttonLabel,
  variant,
  ariaLabel,
  className,
  highlightKey,
  highlightBadge,
  openToken,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [popStyle, setPopStyle] = useState<CSSProperties | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const firstToken = useRef(openToken);
  /* True only when the CURRENT open came from openToken (the upsell button).
     The badge/highlight shows on every open; the auto-scroll + pulse must not
     (owner 2026-08-15: every normal open was yanking the list to 12×18). */
  const externalOpen = useRef(false);

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    externalOpen.current = false;
    const r = rootRef.current?.getBoundingClientRect();
    setPopStyle(r ? popStyleFor(r) : null);
    setOpen(true);
  };

  /* Opened from the upsell button: the list must land ON the suggested row,
     not at the top of 40 sizes (owner 2026-08-15 — the earlier version opened
     the picker and left the customer hunting). */
  useEffect(() => {
    if (openToken === undefined || openToken === firstToken.current) return;
    firstToken.current = openToken;
    externalOpen.current = true;
    const r = rootRef.current?.getBoundingClientRect();
    if (r && typeof window !== 'undefined') setPopStyle(popStyleFor(r));
    setOpen(true);
  }, [openToken]);

  useEffect(() => {
    if (!open) return;
    /* Upsell-button opens centre on the SUGGESTED row and pulse it; normal
       opens centre on the CURRENT selection with no pulse (owner 2026-08-16:
       a 16×20 customer opened the list at the very top and had to scroll past
       every small size just to consider one step up — that reads as a nudge
       to downsize). */
    const external = externalOpen.current;
    externalOpen.current = false;
    const targetKey = external ? highlightKey : value;
    if (!targetKey) return;
    /* Double rAF: the mobile bottom sheet needs a layout pass before offsets
       are real. Scroll the POPOVER itself (not scrollIntoView, which also
       scrolls the page behind the fixed sheet), then pulse the row so the eye
       lands on it (owner 2026-08-15: "opens but doesn't focus" bug — the ref
       was never attached, so this code was aiming at nothing). */
    let id2 = 0;
    const id = window.requestAnimationFrame(() => {
      id2 = window.requestAnimationFrame(() => {
        const pop = popRef.current;
        const row = pop?.querySelector<HTMLElement>(`[data-optkey="${CSS.escape(targetKey)}"]`);
        if (!pop || !row) return;
        pop.scrollTop = Math.max(0, row.offsetTop - pop.clientHeight / 2 + row.offsetHeight / 2);
        if (!external) return;
        row.classList.remove('dd__opt--pulse');
        void row.offsetWidth; // restart the animation when reopened
        row.classList.add('dd__opt--pulse');
      });
    });
    return () => {
      window.cancelAnimationFrame(id);
      window.cancelAnimationFrame(id2);
    };
  }, [open, highlightKey, value]);

  /** Popover geometry for a trigger rect — shared by the click path and the
   *  external open (upsell button), so both position identically. */
  const popStyleFor = (r: DOMRect): CSSProperties | null => {
    if (typeof window === 'undefined') return null;
    // Same test as MOBILE_QUERY, not a second hardcoded 767: a landscape
    // phone is 844px wide, and anchoring a size list under the trigger in a
    // 390px-tall viewport gives it the 180px minimum and nothing more.
    if (window.matchMedia(MOBILE_QUERY).matches) {
      // Phones (owner 2026-07-27): a BOTTOM SHEET taking ~70% of the screen.
      return {
        position: 'fixed',
        left: 8,
        right: 8,
        bottom: 8,
        top: 'auto',
        minWidth: 0,
        maxWidth: 'none',
        maxHeight: Math.round(window.innerHeight * 0.7),
        zIndex: 600,
      };
    }
    const left = Math.max(6, Math.min(r.left, window.innerWidth - 306));
    return {
      position: 'fixed',
      top: r.bottom + 6,
      left,
      minWidth: Math.min(Math.max(r.width, 220), window.innerWidth - 36),
      maxHeight: Math.min(420, Math.max(180, window.innerHeight - r.bottom - 18)),
      zIndex: 600,
    };
  };


  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    // Any outside scroll/resize would detach the FIXED popover — close it.
    // (The popover's own internal scrolling is inside rootRef and stays open.)
    const onScroll = (e: Event) => {
      if (rootRef.current && e.target instanceof Node && rootRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    // capture so the dropdown wins over any open modal's Escape handler
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`dd dd--${variant}${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        className="dd__btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={toggle}
      >
        <span className="dd__btn-label">{buttonLabel}</span>
        <span className="dd__caret" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div ref={popRef} className="dd__pop" role="listbox" aria-label={ariaLabel} style={popStyle ?? undefined}>
          {groups.map((group) => (
            <div key={group.label} className="dd__group">
              <div className="dd__group-label">{group.label}</div>
              {group.options.map((opt) => (
                <button
                  key={opt.key}
                  data-optkey={opt.key}
                  type="button"
                  role="option"
                  aria-selected={opt.key === value}
                  aria-disabled={opt.disabled || undefined}
                  className={`dd__opt${opt.key === value ? ' dd__opt--on' : ''}${
                    opt.disabled ? ' dd__opt--disabled' : ''
                  }${opt.key === highlightKey ? ' dd__opt--hero' : ''}`}
                  onClick={() => {
                    if (opt.disabled) return;
                    onChange(opt.key);
                    setOpen(false);
                  }}
                >
                  <span className="dd__opt-label">
                    {opt.label}
                    {opt.key === highlightKey && highlightBadge && (
                      <span className="dd__badge dd__badge--fit">{highlightBadge}</span>
                    )}
                    {opt.key !== highlightKey && opt.badge && (
                      <span className={`dd__badge${opt.badgeTone ? ` dd__badge--${opt.badgeTone}` : ''}`}>
                        {opt.badge}
                      </span>
                    )}
                    {opt.badge2 && <span className="dd__badge dd__badge--soft">{opt.badge2}</span>}
                    {opt.hint && <span className="dd__hint">{opt.hint}</span>}
                  </span>
                  {opt.right && (
                    <span className={`dd__price${opt.rightMuted ? ' dd__price--muted' : ''}`}>
                      {opt.right}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
